import Phaser from 'phaser';
import { ActionRunner } from '../core/ActionRunner';
import { anyJustDown } from '../core/Keybinds';
import type { GameKeys } from '../core/Keybinds';
import { DialogueDef, DialogueNode } from '../types/data';
import { QuestSystem } from './QuestSystem';
import { ActionPanel } from '../ui/ActionPanel';
import { ChoiceUI } from '../ui/ChoiceUI';
import { DialogueUI } from '../ui/DialogueUI';

export class DialogueSystem {
  private active = false;
  private dialogue?: DialogueDef;
  private currentNode?: DialogueNode;
  private selectedChoiceIndex = 0;
  private onComplete?: () => void;
  private lastTime = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly keys: GameKeys,
    private readonly dialogueUI: DialogueUI,
    private readonly choiceUI: ChoiceUI,
    private readonly actionPanel: ActionPanel,
  ) {
    this.choiceUI.setOnChoose((index) => this.choose(index));
  }

  start(dialogue: DialogueDef, onComplete?: () => void) {
    this.active = true;
    this.dialogue = dialogue;
    this.onComplete = onComplete;
    this.selectedChoiceIndex = 0;
    this.lastTime = this.scene.time.now;
    this.gotoNode(dialogue.startNodeId);
  }
  isActive() { return this.active; }

  update() {
    const delta = Math.max(0, this.scene.time.now - this.lastTime);
    this.lastTime = this.scene.time.now;
    this.dialogueUI.update(delta);
    if (!this.active || !this.currentNode) return;
    if (this.currentNode.type === 'say') {
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        if (!this.dialogueUI.isFullyRevealed()) this.dialogueUI.revealAll();
        else this.currentNode.next ? this.gotoNode(this.currentNode.next) : this.finish();
      }
      return;
    }
    if (this.currentNode.type === 'choice') {
      if (anyJustDown(this.keys.choiceUp, ['up'])) { this.selectedChoiceIndex = Math.max(0, this.selectedChoiceIndex - 1); this.renderCurrentNode(); }
      if (anyJustDown(this.keys.choiceDown, ['down'])) { this.selectedChoiceIndex = Math.min(this.currentNode.choices.length - 1, this.selectedChoiceIndex + 1); this.renderCurrentNode(); }
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        if (!this.dialogueUI.isFullyRevealed()) { this.dialogueUI.revealAll(); return; }
        const selected = this.currentNode.choices[this.selectedChoiceIndex];
        selected?.next ? this.gotoNode(selected.next) : this.finish();
      }
      return;
    }
    if (this.currentNode.type === 'action' && anyJustDown(this.keys.confirm, ['confirm', 'interact'])) this.runActionNode(this.currentNode);
  }

  private gotoNode(nodeId: string) {
    const node = this.dialogue?.nodes.find((item) => item.id === nodeId);
    if (!node) { this.finish(); return; }
    this.currentNode = node; this.selectedChoiceIndex = 0; this.renderCurrentNode();
    if (node.type === 'action') this.runActionNode(node);
  }

  private renderCurrentNode() {
    const node = this.currentNode; if (!node) return;
    this.choiceUI.hide();
    if (node.type === 'say') { this.dialogueUI.show(node.speaker, node.text, node.portraitKey); return; }
    if (node.type === 'choice') { this.dialogueUI.show('선택', node.text ?? '무엇을 할까요?', node.portraitKey); this.choiceUI.show(node.choices.map((choice) => choice.text), this.selectedChoiceIndex); return; }
    if (node.type === 'action') this.dialogueUI.show('실행', node.text ?? '작업을 실행합니다...', node.portraitKey, true);
  }

  private async runActionNode(node: Extract<DialogueNode, { type: 'action' }>) {
    const logs = await ActionRunner.runActions(node.actions);
    this.actionPanel.flash(logs);
    node.next ? this.gotoNode(node.next) : this.finish();
  }

  private choose(index: number) {
    if (!this.active || this.currentNode?.type !== 'choice') return;
    this.selectedChoiceIndex = Phaser.Math.Clamp(index, 0, this.currentNode.choices.length - 1);
    if (!this.dialogueUI.isFullyRevealed()) this.dialogueUI.revealAll();
    const selected = this.currentNode.choices[this.selectedChoiceIndex];
    selected?.next ? this.gotoNode(selected.next) : this.finish();
  }

  private finish() {
    const completedDialogueId = this.dialogue?.id;
    this.active = false; this.dialogue = undefined; this.currentNode = undefined; this.choiceUI.hide(); this.dialogueUI.hide(); this.onComplete?.(); this.onComplete = undefined;
    if (completedDialogueId) QuestSystem.record('dialogue_complete', completedDialogueId);
  }
}
